import React, { useMemo, useState } from 'react';
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';

import { ErrorAlert } from '../../ui-elements/ErrorAlert';
import { useTranslations } from '../../../translations/useTranslations';
import { useCommonOptionsStore } from '../../../store';
import { sanitizeErrors } from '../../../utils/errors';
import {
  NEXT_ACTION_TYPE_STRIPE_CONFIRM,
  type ProcessorsType,
  SupportedPaymentMethods,
} from '../../../constants';
import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { stripePaymentMethodHashPayloadFactory } from '../../../factories/query-payment-method-hash-payload/stripePaymentMethodHashPayloadFactory';
import { useNextActionHandler } from '../../../handlers/nextActionHandler';

import type { StripeExpressCheckoutElementOptions } from '@stripe/stripe-js';
import type {
  FormInputsType,
  NextAction,
  PaymentElementResponse,
} from '../../../@types';
import type { PaymentFormWithStripeProps } from '../../../propTypes';

type GooglePayFormPropTypes = PaymentFormWithStripeProps & {
  payMethod: string;
  processor: ProcessorsType;
};

const layoutProps: Record<string, any> = {
  gap: 6,
  columnGap: 4,
};

export const GooglePayForm = React.forwardRef(
  (
    {
      onLoadStart,
      onLoadEnd,
      onError,
      onSubmit,
      payMethod,
    }: GooglePayFormPropTypes,
    forwardRef: any,
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const nextActionHandler = useNextActionHandler();

    const ElementOptions: StripeExpressCheckoutElementOptions = useMemo(
      () => ({
        layout: {
          maxColumns: 1,
          maxRows: 1,
        },
        paymentMethodOrder: [payMethod],
        buttonHeight: 40,
        wallets: {
          applePay:
            payMethod === SupportedPaymentMethods.APPLEPAY ? 'always' : 'never',
          googlePay:
            payMethod === SupportedPaymentMethods.GOOGLEPAY
              ? 'always'
              : 'never',
        },
      }),
      [payMethod],
    );

    const lang = useCommonOptionsStore(state => state.lang);
    const propertyId = useCommonOptionsStore(state => state.propertyId);
    const apiKey = useCommonOptionsStore(state => state.apiKey);
    const { apiUrl } = useCommonOptionsStore();

    const [t] = useTranslations(lang);

    const formMethods = useForm<FormInputsType>({
      mode: 'onChange',
    });

    const [methodUnavailable, setMethodUnavailable] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>();
    const [isReady, setReady] = useState<boolean>(false);
    const { handleSubmit, formState } = formMethods;

    const { errors, isLoading, isSubmitting } = formState;

    const errCount = React.useMemo(
      () => (errors ? Object.keys(errors).length : 0),
      [errors],
    );

    React.useEffect(() => {
      if (onLoadStart && isLoading) onLoadStart();
      if (onLoadEnd && isLoading) onLoadEnd();
    }, [isLoading, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (onLoadStart && isSubmitting) onLoadStart();
      if (onLoadEnd && isSubmitting) onLoadEnd();
    }, [isSubmitting, onLoadStart, onLoadEnd]);

    React.useEffect(() => {
      if (onError && errCount) onError(sanitizeErrors(errors));
    }, [onError, errors, errCount]);

    const submitHandler: SubmitHandler<FormInputsType> = async (
      formData: any,
    ) => {
      const data = { ...formData };

      if (onSubmit) {
        const responseData: PaymentElementResponse = {
          ...data,
          paymentMethod: payMethod,
        };

        onSubmit({ ...responseData });
      }
    };

    const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
      if (onError) onError(sanitizeErrors(errors));
    };

    React.useImperativeHandle(forwardRef, () => ({
      submit() {
        handleSubmit(submitHandler, submitErrorHandler)();
      },
      async validate() {
        return true;
      },
    }));

    /**
     * Run on payment confirmation
     */
    const onConfirm = async () => {
      if (!stripe || !elements) return;

      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || t('paymentMethodError'));
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) setErrorMessage(error.message || t('paymentMethodError'));

      /**
       * Get payment method hash.
       */
      const { hash: paymentMethodHash, error: hashError } =
        await queryPaymentMethodHash(
          +propertyId,
          apiKey,
          stripePaymentMethodHashPayloadFactory(paymentMethod.id),
          apiUrl,
        );
      if (hashError && onError) onError(error);

      let nextAction: NextAction;
      if (onSubmit) {
        nextAction = await onSubmit({
          paymentMethod: payMethod,
          paymentMethodId: paymentMethod.id,
          paymentMethodHash,
        });

        await nextActionHandler(nextAction);
      }

      if (!nextAction || nextAction.type !== NEXT_ACTION_TYPE_STRIPE_CONFIRM)
        return;

      // for backwards compatibility
      const nextActionDetails =
        nextAction?.details || nextAction?.stripe_confirmation_details;
      if (!nextActionDetails.client_secret) return;

      /** @TODO Figure out why "as any" was required below. This may be a broken request. */
      const { error: confirmError } = await stripe.confirmPayment({
        clientSecret: nextActionDetails.client_secret,
        confirmParams: {
          payment_method: paymentMethod.id,
          return_url: nextActionDetails.url || nextActionDetails.return_url, // for backwards compatibility
        } as any,
      });

      if (confirmError) {
        if (onLoadEnd) onLoadEnd();
        setErrorMessage(confirmError.message || t('paymentMethodError'));
      }

      if (nextAction?.onSuccess) nextAction.onSuccess();
    };

    const onReady = ({ availablePaymentMethods }: any) => {
      setReady(true);
      if (!availablePaymentMethods) setMethodUnavailable(true);
    };

    return (
      <>
        <FormProvider {...formMethods}>
          {!isReady && (
            <Center w="100%" h="100%" p={4}>
              <Spinner
                thickness="4px"
                emptyColor="gray.200"
                color="blue.500"
                size="lg"
              />
            </Center>
          )}
          <Box paddingTop={(layoutProps as any).gap}>
            {errorMessage && (
              <ErrorAlert>{errorMessage || t('paymentMethodError')}</ErrorAlert>
            )}
            {methodUnavailable &&
              payMethod === SupportedPaymentMethods.GOOGLEPAY &&
              t('googlePayNotAvailableMessage')}
            {methodUnavailable &&
              payMethod === SupportedPaymentMethods.APPLEPAY &&
              t('applePayNotAvailableMessage')}
            <ExpressCheckoutElement
              options={ElementOptions}
              onConfirm={onConfirm}
              onReady={onReady}
            />
          </Box>
        </FormProvider>
      </>
    );
  },
);
